/**
 * Theme toggle — switches between light and dark mode.
 *
 * Persists preference to localStorage. Defaults to system preference
 * on first visit via prefers-color-scheme media query.
 */

import { useState, useEffect } from 'react'
import { Moon, Sun } from 'lucide-react'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'defconai-theme'

/** Get the initial theme from localStorage or system preference. */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  // Default to dark — the DefconAI platform is dark-first (ARTIV C2 aesthetic)
  return 'dark'
}

/** Apply theme to the document root. */
function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
}

/**
 * Theme toggle button with sun/moon icons.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    applyTheme(theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  // Apply theme on mount (before first render completes)
  useEffect(() => {
    applyTheme(getInitialTheme())
  }, [])

  const toggle = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'))

  return (
    <button
      onClick={toggle}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      className="p-2 rounded-lg transition-colors cursor-pointer text-content-secondary bg-transparent border-none hover:bg-surface-tertiary"
    >
      {theme === 'light' ? (
        <Moon className="w-[18px] h-[18px]" />
      ) : (
        <Sun className="w-[18px] h-[18px]" />
      )}
    </button>
  )
}
